import { Fragment, useMemo, useState } from 'react';
import { FormErrorIcon, NumberInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import { FlexBox, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import {
  type ComponentOrReferenceFormattingRule,
  type FormattingContextState,
} from '../types';
import { ensureColorRangeValue } from '../ensureUpdatedRuleValue';
import {
  ColorRangeFormatting,
  ColorRangeType,
  ColorRangePoint,
  ColorRangeMidPoint,
} from '@ardoq/api-types';
import { ColorRangePreview } from './ColorRangePreview';
import { FormattingColorPicker } from '../../FormattingColorPicker';
import { RowHeader, NumberInputPostfix } from '../../../atoms';
import { getDefaultColorRangeNumberValues } from '../getDefaultColorRangeNumberValues';
import { ValueWarningMessage } from './ValueWarningMessage';
import { buildColorWarningKey } from '../colorWarnings';
import { getColorRangePointError } from '../getColorRangePointError';
import { getColorRangeModeLimits } from '../colorRangeModeLimits';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';
import { type I18nLocale, t } from '@ardoq/i18n';
import { useFormattingContext } from '../useFormattingContext';

type ColorRangePointKeys = 'min' | 'mid' | 'max';
type ColorRangeValueInputProps = {
  rule: ComponentOrReferenceFormattingRule;
  formattingContext: FormattingContextState;
  isSidebar?: boolean;
  colorPaletteAlignment?: 'left' | 'right';
  menuPortalTarget?: HTMLElement | null;
};

const getValueDescriptionText = (i18nLocale: I18nLocale): string =>
  t(
    i18nLocale,
    'perspectives:colorRangeValue.description',
    'Decimals can be typed in. Default values are the minimum and maximum in your dataset.'
  );

const getModeOptions = (
  i18nLocale: I18nLocale
): { label: string; value: ColorRangeType }[] => [
  {
    label: t(i18nLocale, 'perspectives:colorRangeValue.mode.number', 'Number'),
    value: 'number',
  },
  {
    label: t(
      i18nLocale,
      'perspectives:colorRangeValue.mode.percent',
      'Percent'
    ),
    value: 'percent',
  },
  {
    label: t(
      i18nLocale,
      'perspectives:colorRangeValue.mode.percentile',
      'Percentile'
    ),
    value: 'percentile',
  },
];

// Ensures that the value for the given key is within the limits of the selected mode
const getSafeColorRangeValue = (
  mode: ColorRangeType,
  key: ColorRangePointKeys,
  nextValue: unknown
): number | undefined => {
  if (key === 'mid' && !Number.isFinite(nextValue)) {
    return undefined;
  }

  const value = Number(nextValue);

  if (Number.isFinite(value)) {
    const { min: modeMin, max: modeMax } = getColorRangeModeLimits(mode);

    if (value > modeMax) return Number.isFinite(modeMax) ? modeMax : undefined;
    if (value < modeMin) return Number.isFinite(modeMin) ? modeMin : undefined;
  }

  return Number.isFinite(value) ? value : undefined;
};

const buildPostfixNode = (
  hasError: boolean,
  mode: ColorRangeType
): React.ReactNode => {
  if (mode === 'percent') {
    return hasError ? (
      <NumberInputPostfix>
        <FlexBox align="center" gap="small" style={{ whiteSpace: 'nowrap' }}>
          <Icon
            iconName={IconName.ERROR}
            iconSize={IconSize.MEDIUM}
            color={colors.iconError}
          />
          %
        </FlexBox>
      </NumberInputPostfix>
    ) : (
      <NumberInputPostfix>%</NumberInputPostfix>
    );
  }

  return hasError ? <FormErrorIcon iconSize={IconSize.MEDIUM} /> : null;
};

export const ColorRangeValue = ({
  rule,
  formattingContext: { updateRule, datasetData, getEntityFieldValue, ruleInfo },
  isSidebar = false,
  colorPaletteAlignment = 'right',
  menuPortalTarget,
}: ColorRangeValueInputProps) => {
  const { i18nLocale } = useFormattingContext();
  const { colorWarnings } = ruleInfo[rule.__id] || {};
  const normalizedValue = ensureColorRangeValue(rule.value);
  const { mode, min, mid, max } = normalizedValue;

  // Track last touched input to show internal warning for color range
  const [lastTouchedInput, setLastTouchedInput] = useState<
    ColorRangePointKeys | undefined
  >(undefined);

  const onValueChange = (newValue: ColorRangeFormatting) =>
    updateRule(rule.__id, { value: newValue });

  const handleModeChange = (newMode: ColorRangeType) => {
    const { min: modeMin, max: modeMax } = getColorRangeModeLimits(newMode);
    const nextValues: Partial<ColorRangeFormatting> =
      newMode === 'number'
        ? getDefaultColorRangeNumberValues(
            rule,
            normalizedValue,
            datasetData,
            getEntityFieldValue
          )
        : {
            min: { ...normalizedValue.min, value: modeMin },
            mid: { ...normalizedValue.mid, value: undefined },
            max: { ...normalizedValue.max, value: modeMax },
          };

    onValueChange({
      ...normalizedValue,
      mode: newMode,
      ...(nextValues || {}),
    });
  };

  const updateColorRangePoint = (
    key: ColorRangePointKeys,
    partial: Partial<{ color: string; value?: number }>
  ) =>
    onValueChange({
      ...normalizedValue,
      [key]: {
        ...normalizedValue[key],
        ...partial,
      },
    });

  const currentLimits = useMemo(() => getColorRangeModeLimits(mode), [mode]);

  const isAtMin = (value?: number) =>
    value !== undefined && value <= currentLimits.min;
  const isAtMax = (value?: number) =>
    value !== undefined && value >= currentLimits.max;

  const numberValuePlaceholder = t(
    i18nLocale,
    'perspectives:colorRangeValue.placeholder.number',
    'type a number value'
  );
  const optionalPlaceholder = t(
    i18nLocale,
    'perspectives:colorRangeValue.placeholder.optional',
    'optional'
  );

  const valueRows: {
    colorRangePoint?: ColorRangePoint | ColorRangeMidPoint;
    pointKey: ColorRangePointKeys;
    placeholder: string;
  }[] = [
    {
      colorRangePoint: min,
      placeholder: numberValuePlaceholder,
      pointKey: 'min',
    },
    { colorRangePoint: mid, placeholder: optionalPlaceholder, pointKey: 'mid' },
    {
      colorRangePoint: max,
      placeholder: numberValuePlaceholder,
      pointKey: 'max',
    },
  ];

  const rangePointError = getColorRangePointError(
    i18nLocale,
    normalizedValue,
    lastTouchedInput
  );

  const pointLabels: Record<ColorRangePointKeys, string> = {
    min: t(
      i18nLocale,
      'perspectives:colorRangeValue.pointLabel.min',
      'Min point'
    ),
    mid: t(
      i18nLocale,
      'perspectives:colorRangeValue.pointLabel.mid',
      'Mid point'
    ),
    max: t(
      i18nLocale,
      'perspectives:colorRangeValue.pointLabel.max',
      'Max point'
    ),
  };

  return (
    <Stack gap="small" flex={1}>
      {isSidebar ? (
        <>
          <RowHeader>
            {t(i18nLocale, 'perspectives:colorRangeValue.valueHeader', 'Value')}
          </RowHeader>
          <Text variant="text2" color="textModerate">
            {getValueDescriptionText(i18nLocale)}
          </Text>
        </>
      ) : null}
      <FlexBox align="center" gap="medium" flexWrap>
        <FlexBox flex={1} flexDirection="column">
          <ColorRangePreview
            colorRange={normalizedValue}
            i18nLocale={i18nLocale}
          />
        </FlexBox>
        <FlexBox flexDirection="column" gap="xsmall" flex={1}>
          <Text variant="caption" color="textSubtle">
            {t(
              i18nLocale,
              'perspectives:colorRangeValue.pointFormat',
              'Point format'
            )}
          </Text>
          <Select
            aria-label={t(
              i18nLocale,
              'perspectives:colorRangeValue.modeAriaLabel',
              'Color range mode'
            )}
            options={getModeOptions(i18nLocale)}
            value={mode}
            onValueChange={modeValue =>
              modeValue && handleModeChange(modeValue)
            }
            placeholder={t(
              i18nLocale,
              'perspectives:colorRangeValue.selectModePlaceholder',
              'Select mode'
            )}
            dataTestId="color-range-mode-select"
            menuPortalTarget={menuPortalTarget}
          />
        </FlexBox>
      </FlexBox>

      <FlexBox flexDirection="column" gap="medium">
        {valueRows.map(({ colorRangePoint, placeholder, pointKey }) => {
          const pointValue = colorRangePoint?.value ?? undefined;

          const rangeError =
            rangePointError?.point === pointKey
              ? rangePointError.message
              : undefined;

          const colorWarning =
            !rangeError && colorWarnings && colorRangePoint?.color
              ? colorWarnings[
                  buildColorWarningKey(colorRangePoint.color, pointKey)
                ]
              : undefined;

          const showRangeError = Boolean(rangeError);
          const showColorWarning = Boolean(colorWarning) && !showRangeError;

          const postfixNode = colorWarning
            ? null
            : buildPostfixNode(showRangeError, mode);

          return (
            <Fragment key={pointKey}>
              <Stack gap="xsmall">
                <Text variant="caption" color="textSubtle">
                  {pointLabels[pointKey]}
                </Text>
                <FlexBox align="flex-start" gap="small">
                  <FormattingColorPicker
                    dataTestId={`color-range-${pointKey}-color`}
                    value={colorRangePoint?.color || ''}
                    onValueChange={color => {
                      setLastTouchedInput(pointKey);
                      updateColorRangePoint(pointKey, { color });
                    }}
                    variant="swatch"
                    hasWarning={showColorWarning}
                    isNewConditionalFormatting
                    colorPaletteAlignment={colorPaletteAlignment}
                  />
                  <FlexBox flex={1} flexDirection="column" gap="xxsmall">
                    <NumberInput
                      allowDecimals={mode === 'number'}
                      value={pointValue}
                      onValueChange={num => {
                        setLastTouchedInput(pointKey);
                        updateColorRangePoint(pointKey, {
                          value: Number.isFinite(num) ? num : undefined,
                        });
                      }}
                      placeholder={placeholder}
                      dataTestId={`color-range-${pointKey}-input`}
                      upArrowDisabled={isAtMax(pointValue)}
                      downArrowDisabled={isAtMin(pointValue)}
                      hasError={showRangeError}
                      hasWarning={showColorWarning}
                      hideWarningIcon={showRangeError || !showColorWarning}
                      postfixNode={postfixNode}
                      onFocus={() => setLastTouchedInput(pointKey)}
                      onBlurTransformValue={value =>
                        getSafeColorRangeValue(mode, pointKey, value)
                      }
                    />
                    {showRangeError && (
                      <ValueWarningMessage
                        message={rangeError}
                        color="textError"
                      />
                    )}
                  </FlexBox>
                </FlexBox>
                {showColorWarning && (
                  <ValueWarningMessage message={colorWarning} />
                )}
              </Stack>
            </Fragment>
          );
        })}
      </FlexBox>
    </Stack>
  );
};
